import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { isDesktopLayout } from "../../common/hooks/useIsDesktopView";
import {
  CONTENT_PROGRESS_MILESTONES,
  getContentLocation,
  getContentOrigin,
  trackContentProgressMilestone,
  trackContentReadSummary,
  type ConsumedContentType,
  type ContentExitAction,
  type ContentOpenContext,
  type ContentProgressMilestone,
  type ContentReadDetails,
} from "./base.analytics";

// One open of one content item, on the common consumption scale
// (docs/content-consumption-tracking.md). The page says what it shows and
// reports its current percentage; this hook owns the clocks, the milestones
// and the exit, and sends `content_progress_milestone` and
// `content_read_summary`. useEffect/useRef live here, in a dedicated hook,
// never in a business component.

/** What a page shows. A new id is a new open. */
export type ContentProgressSubject = {
  contentType: ConsumedContentType;
  contentId: string;
};

/**
 * The first screen of a content item counts only once the reader stayed on it
 * this long: an open closed at once is 0%, not the share its first screen
 * happens to show.
 */
export const SETTLE_MS = 3000;

type Open = {
  context: ContentOpenContext;
  openedAt: number;
  visibleMs: number;
  visibleSince: number | null;
  maxPercent: number;
  sent: Set<ContentProgressMilestone>;
  exitAction: ContentExitAction | null;
  settled: boolean;
};

function subjectKey(subject: ContentProgressSubject): string {
  return `${subject.contentType}:${subject.contentId}`;
}

function runningVisibleMs(open: Open, now: number): number {
  return (
    open.visibleMs + (open.visibleSince === null ? 0 : now - open.visibleSince)
  );
}

export function useContentProgress(params: {
  subject: ContentProgressSubject | null;
  /**
   * True when the open ends as the app goes to the background: a page the
   * reader cannot see is not being read. False for the audio player, which
   * keeps playing there.
   */
  endOnHidden: boolean;
  /**
   * An open starts. `new`: reset the state of the page. `resumed`: the same
   * item after the background, or after a spurious `pagehide`; keep what the
   * reader already consumed, which the open's percentage still holds.
   */
  onStart: (start: "new" | "resumed") => void;
  /** The reader stayed `SETTLE_MS` on the open; null when nothing settles. */
  onSettled: (() => void) | null;
  /** The open ends: the figures of its type, read once. */
  onFinish: (open: ContentOpenContext) => ContentReadDetails;
}): {
  reportPercent: (percent: number) => void;
  noteExit: (action: ContentExitAction) => void;
} {
  const { subject } = params;
  const key = subject === null ? null : subjectKey(subject);

  const paramsRef = useRef(params);
  paramsRef.current = params;
  // The key of the current render, read by the cleanup of the previous open:
  // another key means the reader moved on to another content item.
  const keyRef = useRef(key);
  keyRef.current = key;
  const openRef = useRef<Open | null>(null);
  // An open ended by the background, resumed at its percentage on return.
  const resumeRef = useRef<{ key: string; percent: number } | null>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSettle = useCallback(() => {
    if (settleTimerRef.current === null) return;
    clearTimeout(settleTimerRef.current);
    settleTimerRef.current = null;
  }, []);

  const armSettle = useCallback(() => {
    clearSettle();
    const open = openRef.current;
    if (open === null || open.settled) return;
    if (document.visibilityState !== "visible") return;
    const onSettled = paramsRef.current.onSettled;
    if (onSettled === null) return;
    const remaining = SETTLE_MS - runningVisibleMs(open, Date.now());
    settleTimerRef.current = setTimeout(
      () => {
        settleTimerRef.current = null;
        if (openRef.current !== open) return;
        open.settled = true;
        onSettled();
      },
      Math.max(0, remaining)
    );
  }, [clearSettle]);

  const begin = useCallback(
    (opened: ContentProgressSubject, resumed: { percent: number } | null) => {
      const seedPercent = resumed === null ? 0 : resumed.percent;
      const now = Date.now();
      const origin = getContentOrigin();
      openRef.current = {
        context: {
          contentType: opened.contentType,
          contentId: opened.contentId,
          surface: isDesktopLayout() ? "desktop" : "mobile",
          location: getContentLocation(),
          contentOrigin: origin === null ? "public app" : origin,
        },
        openedAt: now,
        visibleMs: 0,
        visibleSince: document.visibilityState === "visible" ? now : null,
        maxPercent: seedPercent,
        // A resumed open does not send again what the first part sent.
        sent: new Set(
          CONTENT_PROGRESS_MILESTONES.filter(
            (milestone) => milestone <= seedPercent
          )
        ),
        exitAction: null,
        settled: false,
      };
      paramsRef.current.onStart(resumed === null ? "new" : "resumed");
      armSettle();
    },
    [armSettle]
  );

  const finish = useCallback(
    (defaultExit: ContentExitAction): Open | null => {
      const open = openRef.current;
      if (open === null) return null;
      openRef.current = null;
      clearSettle();
      const now = Date.now();
      trackContentReadSummary(open.context, {
        progressPercent: open.maxPercent,
        durationSeconds: Math.round((now - open.openedAt) / 1000),
        visibleSeconds: Math.round(runningVisibleMs(open, now) / 1000),
        exitAction: open.exitAction === null ? defaultExit : open.exitAction,
        details: paramsRef.current.onFinish(open.context),
      });
      return open;
    },
    [clearSettle]
  );

  // A layout effect: when the page swaps to the next item, the open of the
  // previous one ends in the same commit, before the observers of the page
  // (`observeDwell`) see the new item's elements.
  useLayoutEffect(() => {
    if (subject === null) return;
    resumeRef.current = null;
    begin(subject, null);
    return () => {
      const next = keyRef.current;
      finish(next !== null && next !== key ? "next_content" : "unknown");
    };
  }, [key, begin, finish]);

  useEffect(() => {
    function handleVisibilityChange() {
      const now = Date.now();
      const open = openRef.current;
      if (document.visibilityState === "hidden") {
        clearSettle();
        if (open === null) return;
        if (open.visibleSince !== null) {
          open.visibleMs += now - open.visibleSince;
          open.visibleSince = null;
        }
        if (!paramsRef.current.endOnHidden) return;
        finish("app_background");
        resumeRef.current = {
          key: subjectKey(open.context),
          percent: open.maxPercent,
        };
        return;
      }
      if (open !== null) {
        if (open.visibleSince === null) open.visibleSince = now;
        armSettle();
        return;
      }
      resumeShown();
    }
    // The open a page ended in the background or on `pagehide`, started
    // again once the same item shows. Once only: the resume is consumed.
    function resumeShown() {
      const current = paramsRef.current.subject;
      const resume = resumeRef.current;
      if (current === null || resume === null) return;
      resumeRef.current = null;
      if (resume.key !== subjectKey(current)) return;
      begin(current, { percent: resume.percent });
    }
    // Safari restores a page from its cache with `pageshow` and no
    // visibility change (see `useEmailVisitEngagement`).
    function handlePageShow(event: PageTransitionEvent) {
      if (!event.persisted || openRef.current !== null) return;
      resumeShown();
    }
    // The audio player keeps its open in the background: a closed app is
    // its last chance to report it. iOS can fire `pagehide` on a page that
    // lives on and keeps playing: the open then goes on as a resumed one.
    function handlePageHide() {
      const ended = finish("app_background");
      if (ended === null) return;
      const resume = {
        key: subjectKey(ended.context),
        percent: ended.maxPercent,
      };
      // A page resumes like after the background, when it shows again.
      if (paramsRef.current.endOnHidden) {
        resumeRef.current = resume;
        return;
      }
      const current = paramsRef.current.subject;
      if (current === null || resume.key !== subjectKey(current)) return;
      begin(current, { percent: resume.percent });
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("pageshow", handlePageShow);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [armSettle, begin, clearSettle, finish]);

  const reportPercent = useCallback((percent: number) => {
    const open = openRef.current;
    if (open === null) return;
    const rounded = Math.max(0, Math.min(100, Math.round(percent)));
    if (rounded <= open.maxPercent) return;
    open.maxPercent = rounded;
    const elapsedSeconds = Math.round((Date.now() - open.openedAt) / 1000);
    for (const milestone of CONTENT_PROGRESS_MILESTONES) {
      if (milestone > rounded || open.sent.has(milestone)) continue;
      open.sent.add(milestone);
      trackContentProgressMilestone(open.context, {
        progressPercent: milestone,
        elapsedSeconds,
      });
    }
  }, []);

  const noteExit = useCallback((action: ContentExitAction) => {
    const open = openRef.current;
    if (open === null) return;
    open.exitAction = action;
  }, []);

  return { reportPercent, noteExit };
}

/** A tracked element counts as seen after this much visible time. */
export const SEEN_AFTER_MS = 1000;

type DwellEntry = {
  order: number;
  visibleMs: number;
  visibleSince: number | null;
  visibleElements: Set<Element>;
  reached: boolean;
  seen: boolean;
  seenTimer: ReturnType<typeof setTimeout> | null;
};

const DWELL_THRESHOLDS = Array.from({ length: 21 }, (_, step) => step / 20);

/**
 * Visible time per element of `root` that carries `attribute`, keyed by the
 * attribute's value: the article sections (`data-gtm-section`) and the
 * playlist cards. An element is visible when half of it is on screen, or when
 * it fills half the screen (a section taller than two screens never shows
 * half of itself), and only while the app is in the foreground. Elements
 * mounted later (another tab, the next page of a list) join as they appear.
 */
export function observeDwell(
  root: HTMLElement,
  attribute: string,
  onSeen: (key: string) => void
): {
  snapshot: () => {
    key: string;
    order: number;
    seconds: number;
    reached: boolean;
    seen: boolean;
  }[];
  disconnect: () => void;
} {
  const entries = new Map<string, DwellEntry>();
  const keyOfElement = new Map<Element, string>();

  function setVisible(entry: DwellEntry, key: string, now: number) {
    entry.reached = true;
    if (entry.visibleSince !== null) return;
    entry.visibleSince = now;
    if (entry.seen) return;
    entry.seenTimer = setTimeout(
      () => {
        entry.seenTimer = null;
        entry.seen = true;
        onSeen(key);
      },
      Math.max(0, SEEN_AFTER_MS - entry.visibleMs)
    );
  }

  function setHidden(entry: DwellEntry, now: number) {
    if (entry.seenTimer !== null) {
      clearTimeout(entry.seenTimer);
      entry.seenTimer = null;
    }
    if (entry.visibleSince === null) return;
    entry.visibleMs += now - entry.visibleSince;
    entry.visibleSince = null;
  }

  const intersection = new IntersectionObserver(
    (changes) => {
      const now = Date.now();
      for (const change of changes) {
        const key = keyOfElement.get(change.target);
        if (key === undefined) continue;
        const entry = entries.get(key);
        if (entry === undefined) {
          throw new Error(`should not happen: no dwell entry for "${key}"`);
        }
        const isOnScreen =
          change.isIntersecting &&
          (change.intersectionRatio >= 0.5 ||
            change.intersectionRect.height >= window.innerHeight / 2);
        if (isOnScreen) entry.visibleElements.add(change.target);
        else entry.visibleElements.delete(change.target);
        if (
          entry.visibleElements.size > 0 &&
          document.visibilityState === "visible"
        ) {
          setVisible(entry, key, now);
        } else {
          setHidden(entry, now);
        }
      }
    },
    { threshold: DWELL_THRESHOLDS }
  );

  function register() {
    const now = Date.now();
    const elements = Array.from(root.querySelectorAll(`[${attribute}]`));
    for (const [index, element] of elements.entries()) {
      if (keyOfElement.has(element)) continue;
      const key = element.getAttribute(attribute);
      if (key === null) {
        throw new Error(`should not happen: no ${attribute} value`);
      }
      keyOfElement.set(element, key);
      if (!entries.has(key)) {
        entries.set(key, {
          order: index,
          visibleMs: 0,
          visibleSince: null,
          visibleElements: new Set(),
          reached: false,
          seen: false,
          seenTimer: null,
        });
      }
      intersection.observe(element);
    }
    // An element unmounted while on screen (a tab switch) stops its clock.
    for (const [element, key] of keyOfElement) {
      if (root.contains(element)) continue;
      keyOfElement.delete(element);
      intersection.unobserve(element);
      const entry = entries.get(key);
      if (entry === undefined) continue;
      entry.visibleElements.delete(element);
      if (entry.visibleElements.size === 0) setHidden(entry, now);
    }
  }

  function handleVisibilityChange() {
    const now = Date.now();
    for (const [key, entry] of entries) {
      if (document.visibilityState !== "visible") setHidden(entry, now);
      else if (entry.visibleElements.size > 0) setVisible(entry, key, now);
    }
  }

  const mutation = new MutationObserver(register);
  mutation.observe(root, { childList: true, subtree: true });
  document.addEventListener("visibilitychange", handleVisibilityChange);
  register();

  return {
    snapshot: () => {
      const now = Date.now();
      return Array.from(entries, ([key, entry]) => ({
        key,
        order: entry.order,
        seconds: Math.round(
          (entry.visibleMs +
            (entry.visibleSince === null ? 0 : now - entry.visibleSince)) /
            1000
        ),
        reached: entry.reached,
        seen: entry.seen,
      })).sort((a, b) => a.order - b.order);
    },
    disconnect: () => {
      for (const entry of entries.values()) {
        if (entry.seenTimer !== null) clearTimeout(entry.seenTimer);
      }
      intersection.disconnect();
      mutation.disconnect();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    },
  };
}

/**
 * The union of the ranges added to it, as a total length: what was played of
 * a media, or seen of an image, where a part covered twice counts once.
 */
export function createRangeUnion(): {
  add: (from: number, to: number) => void;
  length: () => number;
} {
  let ranges: [number, number][] = [];
  return {
    add: (from, to) => {
      if (to <= from) return;
      const sorted = [...ranges, [from, to] satisfies [number, number]].sort(
        (a, b) => a[0] - b[0]
      );
      const merged: [number, number][] = [];
      for (const range of sorted) {
        const previous = merged.at(-1);
        if (previous !== undefined && range[0] <= previous[1]) {
          previous[1] = Math.max(previous[1], range[1]);
        } else {
          merged.push([range[0], range[1]]);
        }
      }
      ranges = merged;
    },
    length: () => ranges.reduce((total, [from, to]) => total + (to - from), 0),
  };
}

// Two playhead readings further apart than this are a seek, not playback.
const MAX_PLAYED_STEP_SECONDS = 2;

/**
 * The seconds of a media played, from the playhead readings of its player: a
 * jump to the end does not play the middle, and a part played twice counts
 * once. The audio player keeps its own played chunks; the video players only
 * report the playhead.
 */
export function createPlayedRanges(): {
  onPlayhead: (seconds: number) => void;
  playedSeconds: () => number;
} {
  const played = createRangeUnion();
  let last: number | null = null;
  return {
    onPlayhead: (seconds) => {
      if (last !== null) {
        const step = seconds - last;
        if (step > 0 && step <= MAX_PLAYED_STEP_SECONDS)
          played.add(last, seconds);
      }
      last = seconds;
    },
    playedSeconds: played.length,
  };
}

/**
 * The open of a video: the share of it played, from the playhead its player
 * reports. A YouTube video in the native iOS full screen reports nothing:
 * the open keeps its last known share, never an estimate from the clock.
 */
export function useVideoProgress(videoId: string): {
  onPlayhead: (seconds: number) => void;
  onDuration: (seconds: number) => void;
} {
  const playedRef = useRef(createPlayedRanges());
  const durationRef = useRef<number | null>(null);
  const { reportPercent } = useContentProgress({
    subject: { contentType: "video", contentId: videoId },
    endOnHidden: true,
    onStart: (start) => {
      if (start === "resumed") return;
      playedRef.current = createPlayedRanges();
      durationRef.current = null;
    },
    onSettled: null,
    onFinish: () => ({
      mediaDurationSeconds:
        durationRef.current === null ? null : Math.round(durationRef.current),
      playlistItems: null,
      article: null,
    }),
  });

  const reportPlayed = useCallback(() => {
    const duration = durationRef.current;
    if (duration === null) return;
    reportPercent((playedRef.current.playedSeconds() / duration) * 100);
  }, [reportPercent]);

  const onPlayhead = useCallback(
    (seconds: number) => {
      playedRef.current.onPlayhead(seconds);
      reportPlayed();
    },
    [reportPlayed]
  );

  const onDuration = useCallback(
    (seconds: number) => {
      // A stream, or a player still loading, reports no usable length.
      if (!Number.isFinite(seconds) || seconds <= 0) return;
      durationRef.current = seconds;
      // The playhead readings that came before the length count now.
      reportPlayed();
    },
    [reportPlayed]
  );

  return { onPlayhead, onDuration };
}
